'use client';

import type { Plan, Workout } from '@trainme/db/client';

import { Dialog } from '@/app/components/Dialog';
import { useEffect, useMemo, useState } from 'react';
import { HiDownload, HiTrash } from 'react-icons/hi';
import { Tab } from '@headlessui/react';
import { WorkoutChart } from '@/app/workouts/WorkoutChart';
import { WorkoutStatus } from '@/utils/prismaEnums';
import { WorkoutTooltip } from '@/app/components/WorkoutTooltip';
import { cn } from '@/utils/uiUtils';
import { formatDistance } from '@/utils/distanceUtils';
import { secondsToHm } from '@/utils/timeUtils';
import { toast } from 'react-toastify';
import { trpc } from '@/app/api/trpc/client';
import { useUnitSystem } from '@/app/hooks/useUnitSystem';

type PlanWorkoutEntry = {
  week?: number;
  weekIndex?: number;
  day?: number;
  dayIndex?: number;
  sportType?: string;
  activity?: string;
  name?: string;
  workout?: string;
  description?: string;
  duration?: number | string;
  distance?: number | string;
  steps?: string[];
  structure?: string[];
  feeling?: number;
  notes?: string;
  workoutKey?: string;
  date?: string | Date;
};

type PlanWithCoach = Plan & { coachName?: string | null };

type CoachPlanOption = Pick<
  Plan,
  'id' | 'name' | 'author' | 'goal' | 'sportsTypes' | 'lengthWeeks' | 'fileName'
> & { coachName?: string | null; raceDistance?: string | null };

type PlanWeek = {
  week: number;
  // Each day can hold more than one workout (e.g. a brick day: swim + bike +
  // run on the same date) -- an empty array means rest.
  days: Array<PlanWorkoutEntry[]>;
  rawWorkouts: PlanWorkoutEntry[];
};

const parsePlanWorkouts = (planValue: unknown): PlanWorkoutEntry[] => {
  if (Array.isArray(planValue)) {
    return planValue.filter(
      (entry): entry is PlanWorkoutEntry =>
        typeof entry === 'object' && entry !== null
    );
  }
  if (typeof planValue === 'object' && planValue !== null) {
    const payload = planValue as Record<string, unknown>;
    if (Array.isArray(payload.workouts)) {
      return payload.workouts.filter(
        (entry): entry is PlanWorkoutEntry =>
          typeof entry === 'object' && entry !== null
      );
    }
  }
  return [];
};

const parseNumber = (value?: number | string): number | undefined => {
  if (typeof value === 'number') return value;
  if (typeof value === 'string') {
    const parsed = Number(value);
    return Number.isNaN(parsed) ? undefined : parsed;
  }
  return undefined;
};

const buildPreviewWorkout = (
  entry: PlanWorkoutEntry,
  weekNumber: number,
  idx: number
): Workout => {
  const now = new Date();
  const date =
    entry.date instanceof Date
      ? entry.date
      : entry.date
        ? new Date(entry.date)
        : now;

  const duration = parseNumber(entry.duration);
  const distance = parseNumber(entry.distance);

  return {
    id: `plan-preview-${weekNumber}-${idx}-${entry.name ?? entry.workout ?? 'workout'}`,
    sportType: entry.sportType ?? entry.activity ?? 'Run',
    date,
    userId: 'coach-preview',
    createdAt: now,
    updatedAt: now,
    status: WorkoutStatus.PENDING,
    name: entry.name ?? entry.workout ?? 'Coach Workout',
    description: entry.description ?? null,
    duration: duration ?? null,
    distance: distance ?? null,
    steps: (Array.isArray(entry.steps)
      ? entry.steps
      : Array.isArray(entry.structure)
        ? entry.structure
        : null) as string[] | null,
    feeling: entry.feeling ?? null,
    notes: entry.notes ?? null,
  } as Workout;
};

type PlanDetailsDialogProps = {
  plan: PlanWithCoach | null;
  isOpen: boolean;
  onClose: () => void;
  onPlanSelected?: () => void;
  coachId?: string | null;
  onPlanRebuildStart?: (planName: string) => void;
  onPlanRebuildEnd?: () => void;
};

export default function PlanDetailsDialog({
  plan,
  isOpen,
  onClose,
  onPlanSelected,
  coachId,
  onPlanRebuildStart,
  onPlanRebuildEnd,
}: PlanDetailsDialogProps) {
  const [selectedPlanId, setSelectedPlanId] = useState<string | null>(null);
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [isCancelConfirmOpen, setIsCancelConfirmOpen] = useState(false);
  const utils = trpc.useUtils();
  const { mutateAsync: updatePlan, isPending } =
    trpc.settings.updatePlan.useMutation({
      onSuccess: (data) => {
        toast.success('Plan selected successfully.');
        onPlanSelected?.();
        onClose();
      },
      onError: () => {
        toast.error('Error selecting plan.');
      },
    });

  const { mutateAsync: cancelPlan, isPending: isCancelingPlan } =
    trpc.settings.cancelPlan.useMutation({
      onSuccess: async () => {
        toast.success('Plan canceled.');
        await Promise.all([
          utils.settings.getTrainingSettings.invalidate(),
          utils.planInstance.getActive.invalidate(),
        ]);
        onPlanSelected?.();
        onClose();
      },
      onError: () => {
        toast.error('Error canceling plan.');
      },
    });

  const { data: coachPlansData } = trpc.settings.getCoachPlans.useQuery(
    coachId ? { coachId } : undefined,
    {
      enabled: isOpen,
    }
  );

  const coachPlans = (coachPlansData?.plans ?? []) as CoachPlanOption[];
  const currentPlanId = plan?.id ?? null;
  const effectiveSelectedPlanId = selectedPlanId ?? currentPlanId;
  const hasChangedSelection =
    Boolean(effectiveSelectedPlanId) &&
    effectiveSelectedPlanId !== currentPlanId;

  useEffect(() => {
    if (!isOpen) {
      setSelectedPlanId(null);
      setIsConfirmOpen(false);
      return;
    }
    setSelectedPlanId(plan?.id ?? null);
    setIsConfirmOpen(false);
  }, [isOpen, plan?.id]);

  // When there's no active plan, the <select> still has to display some
  // option (the browser auto-shows the first one), but selectedPlanId stays
  // null until the user picks something. That mismatch means re-selecting
  // the already-displayed first option fires no onChange, so the "Choose
  // This Plan" button never enables. Sync state to what's actually shown.
  useEffect(() => {
    if (!isOpen || plan || selectedPlanId || coachPlans.length === 0) {
      return;
    }
    setSelectedPlanId(coachPlans[0].id);
  }, [isOpen, plan, selectedPlanId, coachPlans]);

  const selectedPlan = useMemo(
    () =>
      coachPlans.find((entry) => entry.id === effectiveSelectedPlanId) ?? plan,
    [coachPlans, effectiveSelectedPlanId, plan]
  );

  const handleChoosePlan = () => {
    if (!hasChangedSelection || !selectedPlanId) {
      return;
    }
    setIsConfirmOpen(true);
  };

  const handleConfirmChoosePlan = () => {
    if (!selectedPlanId || !hasChangedSelection) {
      return;
    }
    void (async () => {
      try {
        const previousPlanId = plan?.id ?? null;
        await updatePlan({ id: selectedPlanId });
        const selectedPlan = coachPlans.find(
          (entry) => entry.id === selectedPlanId
        );
        if (selectedPlan) {
          onPlanRebuildStart?.(selectedPlan.name);
        }
        const response = await fetch('/api/chat/coach/plan-rebuild', {
          method: 'POST',
        });
        if (!response.ok) {
          const errorBody = await response.text();
          throw new Error(errorBody || 'Unknown error');
        }
        // The coach service queues the rebuild and returns immediately
        // (it runs the full orchestrator graph, including LLM calls, in the
        // background rather than blocking the request -- see NAO-74
        // follow-up). This poll is now the only thing actually waiting for
        // completion, so it needs a window sized for the real pipeline
        // duration, not just a short buffer for read-lag.
        const deadline = Date.now() + 120000;
        while (Date.now() < deadline) {
          await utils.planInstance.getActive.invalidate();
          const activePlanInstance = await utils.planInstance.getActive.fetch();
          const activePlanId = activePlanInstance?.plan?.id ?? null;
          if (activePlanId && activePlanId !== previousPlanId) {
            break;
          }
          await new Promise((resolve) => setTimeout(resolve, 500));
        }
        await Promise.all([
          utils.settings.getTrainingSettings.invalidate(),
          utils.planInstance.getActive.invalidate(),
        ]);
        toast.success('Plan recreation requested; check back shortly.');
      } catch (error) {
        console.error('Unable to save plan and trigger rebuild', error);
        toast.error('Plan was saved, but rebuild could not be triggered.');
      } finally {
        onPlanRebuildEnd?.();
        setIsConfirmOpen(false);
      }
    })();
  };

  const handleCancelPlan = () => {
    setIsCancelConfirmOpen(true);
  };

  const handleConfirmCancelPlan = () => {
    void (async () => {
      try {
        await cancelPlan();
      } finally {
        setIsCancelConfirmOpen(false);
      }
    })();
  };

  const handleDownloadCSV = () => {
    if (plan?.fileName) {
      const link = document.createElement('a');
      link.href = `/plans/${plan.fileName}`;
      link.download = plan.fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  return (
    <Dialog show={isOpen} onClose={onClose} className='md:max-w-[1280px]'>
      <Dialog.Header>
        <Dialog.Title className='text-xl font-semibold text-gray-900 dark:text-gray-100'>
          {plan ? plan.name : 'Choose a plan'}
        </Dialog.Title>
        {plan && (
          <div className='mt-3 flex flex-wrap items-center gap-3'>
            {plan.goal && (
              <span className='inline-flex items-center rounded-full bg-blue-100 px-3 py-1 text-xs font-medium text-blue-800 dark:bg-blue-900 dark:text-blue-200'>
                Goal: {plan.goal}
              </span>
            )}
            {plan.sportsTypes.map((sport) => (
              <span
                key={sport}
                className='inline-flex items-center rounded-full bg-gray-100 px-3 py-1 text-xs font-medium text-gray-800 dark:bg-gray-700 dark:text-gray-200'
              >
                {sport.replace(/([a-z])([A-Z])/g, '$1 $2')}
              </span>
            ))}
            {plan.lengthWeeks && (
              <span className='inline-flex items-center rounded-full bg-emerald-100 px-3 py-1 text-xs font-medium text-emerald-800 dark:bg-emerald-900 dark:text-emerald-200'>
                {plan.lengthWeeks} Weeks
              </span>
            )}
          </div>
        )}
      </Dialog.Header>

      <Dialog.Body className='p-0 overflow-hidden flex flex-col'>
        {plan ? (
          <PlanDetails plan={plan} />
        ) : (
          <p className='p-6 text-sm text-gray-600 dark:text-gray-300'>
            You don&apos;t have an active plan. Choose one below to get started.
          </p>
        )}
      </Dialog.Body>

      <Dialog.Footer className='!justify-between border-t border-gray-100 dark:border-gray-800 pt-4'>
        <div className='flex items-center gap-3'>
          {plan && (
            <>
              <button
                type='button'
                onClick={handleDownloadCSV}
                className='btn btn-secondary flex items-center gap-2'
                disabled={!plan.fileName}
              >
                <HiDownload className='h-4 w-4' />
                Download CSV
              </button>
              <button
                type='button'
                onClick={handleCancelPlan}
                disabled={isCancelingPlan}
                className='btn btn-danger flex items-center gap-2 disabled:cursor-not-allowed disabled:opacity-50'
              >
                <HiTrash className='h-4 w-4' />
                Cancel Plan
              </button>
            </>
          )}
        </div>
        <div className='flex flex-1 items-center justify-end gap-3'>
          <label
            htmlFor='coach-plan-select'
            className='text-sm text-gray-600 dark:text-gray-300 whitespace-nowrap'
          >
            Choose plan
          </label>
          <select
            id='coach-plan-select'
            value={effectiveSelectedPlanId ?? ''}
            onChange={(event) => setSelectedPlanId(event.target.value)}
            className='w-[260px] max-w-[260px] shrink rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 shadow-sm outline-none transition focus:border-emerald-500 dark:border-gray-700 dark:bg-gray-950 dark:text-gray-100'
          >
            {coachPlans.length > 0 ? (
              coachPlans.map((option) => (
                <option key={option.id} value={option.id}>
                  {option.name}
                </option>
              ))
            ) : plan ? (
              <option value={plan.id}>{plan.name}</option>
            ) : (
              <option value=''>No plans available</option>
            )}
          </select>
          <button
            type='button'
            onClick={handleChoosePlan}
            disabled={isPending || !hasChangedSelection}
            className='btn btn-primary whitespace-nowrap px-8 disabled:cursor-not-allowed disabled:opacity-50'
          >
            Choose This Plan
          </button>
        </div>
      </Dialog.Footer>

      {isConfirmOpen && selectedPlan && (
        <Dialog show={isConfirmOpen} onClose={() => setIsConfirmOpen(false)}>
          <Dialog.Header>
            <Dialog.Title className='text-xl font-semibold text-gray-900 dark:text-gray-100'>
              Confirm plan choice
            </Dialog.Title>
          </Dialog.Header>
          <Dialog.Body className='space-y-3'>
            <p className='text-sm text-gray-700 dark:text-gray-200'>
              You are about to override the coach’s recommended plan and choose
              one manually.
            </p>
            <p className='text-sm text-gray-700 dark:text-gray-200'>
              This plan will be arranged for you, but it may not be the best fit
              for your current training context.
            </p>
            <p className='text-sm font-medium text-gray-900 dark:text-gray-100'>
              If you are sure, I’ll set up {selectedPlan.name} for you.
            </p>
          </Dialog.Body>
          <Dialog.Footer>
            <button
              type='button'
              onClick={() => setIsConfirmOpen(false)}
              className='btn btn-secondary'
            >
              Cancel
            </button>
            <button
              type='button'
              onClick={handleConfirmChoosePlan}
              disabled={isPending}
              className='btn btn-primary px-8'
            >
              {isPending ? 'Saving Plan...' : 'Confirm Choice'}
            </button>
          </Dialog.Footer>
        </Dialog>
      )}

      {isCancelConfirmOpen && plan && (
        <Dialog
          show={isCancelConfirmOpen}
          onClose={() => setIsCancelConfirmOpen(false)}
        >
          <Dialog.Header>
            <Dialog.Title className='text-xl font-semibold text-gray-900 dark:text-gray-100'>
              Cancel plan
            </Dialog.Title>
          </Dialog.Header>
          <Dialog.Body className='space-y-3'>
            <p className='text-sm text-gray-700 dark:text-gray-200'>
              This will cancel {plan.name} from your account. You won’t see this
              plan on your calendar anymore.
            </p>
          </Dialog.Body>
          <Dialog.Footer>
            <button
              type='button'
              onClick={() => setIsCancelConfirmOpen(false)}
              className='btn btn-secondary'
            >
              Cancel
            </button>
            <button
              type='button'
              onClick={handleConfirmCancelPlan}
              disabled={isCancelingPlan}
              className='rounded-lg bg-red-600 px-8 py-2 text-sm font-medium text-white shadow-sm transition hover:bg-red-700 disabled:cursor-not-allowed disabled:opacity-50'
            >
              {isCancelingPlan ? 'Canceling...' : 'Cancel Plan'}
            </button>
          </Dialog.Footer>
        </Dialog>
      )}
    </Dialog>
  );
}

type PlanDetailsProps = {
  plan: PlanWithCoach;
};

export function PlanDetails({ plan }: PlanDetailsProps) {
  const workoutsByWeek = useMemo<PlanWeek[]>(() => {
    if (!plan?.plan) return [];

    const workouts = parsePlanWorkouts(plan.plan);
    const groups: Record<number, PlanWorkoutEntry[]> = {};

    workouts.forEach((w) => {
      const week = w.week ?? w.weekIndex ?? 1;
      if (!groups[week]) groups[week] = [];
      groups[week].push(w);
    });

    return Object.entries(groups)
      .map(([week, list]) => {
        const days: PlanWorkoutEntry[][] = Array.from({ length: 7 }, () => []);
        list.forEach((w) => {
          const dayIdx = (w.day || w.dayIndex || 1) - 1;
          if (dayIdx >= 0 && dayIdx < 7) {
            days[dayIdx].push(w);
          }
        });

        return {
          week: parseInt(week),
          days: days,
          rawWorkouts: list,
        };
      })
      .sort((a, b) => a.week - b.week);
  }, [plan]);
  const displayedWeeks = workoutsByWeek;
  const unitSystem = useUnitSystem();

  return (
    <Tab.Group>
      <Tab.List className='flex space-x-1 rounded-xl bg-slate-100 dark:bg-slate-800 p-1 mx-4 mt-2'>
        <Tab
          className={({ selected }) =>
            cn(
              'w-full rounded-lg py-2.5 text-sm font-medium leading-5',
              'ring-white/60 ring-offset-2 ring-offset-blue-400 focus:outline-none focus:ring-2',
              selected
                ? 'bg-white dark:bg-slate-700 text-blue-700 dark:text-blue-400 shadow'
                : 'text-slate-600 dark:text-slate-400 hover:bg-white/[0.12] hover:text-slate-800'
            )
          }
        >
          Plan Preview
        </Tab>
        <Tab
          className={({ selected }) =>
            cn(
              'w-full rounded-lg py-2.5 text-sm font-medium leading-5',
              'ring-white/60 ring-offset-2 ring-offset-blue-400 focus:outline-none focus:ring-2',
              selected
                ? 'bg-white dark:bg-slate-700 text-blue-700 dark:text-blue-400 shadow'
                : 'text-slate-600 dark:text-slate-400 hover:bg-white/[0.12] hover:text-slate-800'
            )
          }
        >
          Description
        </Tab>
      </Tab.List>
      <Tab.Panels className='mt-2 flex-1 overflow-hidden min-h-[420px] px-4 pb-4'>
        <Tab.Panel className='focus:outline-none'>
          <div className='space-y-6 max-h-[460px] overflow-y-auto pr-2 pb-4 scroll'>
            {displayedWeeks.map((week) => {
              const weekDist = week.rawWorkouts.reduce(
                (acc, w) => acc + (parseFloat(String(w.distance ?? 0)) || 0),
                0
              );
              const weekDur = week.rawWorkouts.reduce(
                (acc, w) => acc + (parseFloat(String(w.duration ?? 0)) || 0),
                0
              );

              return (
                <div
                  key={week.week}
                  className='border-b border-gray-200 dark:border-gray-700 pb-4 last:border-0'
                >
                  <div className='flex justify-between items-end mb-3'>
                    <h4 className='text-lg font-bold text-gray-800 dark:text-gray-200'>
                      Week {week.week}
                    </h4>
                    <div className='text-xs text-gray-500 dark:text-gray-400 space-x-2'>
                      <span>{formatDistance(weekDist * 1000, unitSystem)}</span>
                      <span>•</span>
                      <span>{secondsToHm(weekDur * 60)}</span>
                    </div>
                  </div>
                  <div className='grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-2'>
                    {week.days.map((dayWorkouts, idx) => {
                      if (dayWorkouts.length === 0) {
                        return (
                          <div
                            key={idx}
                            className='bg-gray-50/50 dark:bg-slate-900/30 rounded-lg p-3 border border-dashed border-gray-200 dark:border-gray-800 flex items-center justify-center min-h-[140px]'
                          >
                            <span className='text-[10px] text-gray-300 dark:text-gray-700 font-medium uppercase'>
                              Rest
                            </span>
                          </div>
                        );
                      }
                      return (
                        <div key={idx} className='flex flex-col gap-2'>
                          {dayWorkouts.map((w, wIdx) => {
                            const workoutData = buildPreviewWorkout(
                              w,
                              week.week,
                              idx
                            );
                            return (
                              <WorkoutTooltip key={wIdx} workout={workoutData}>
                                <div className='bg-white dark:bg-slate-900 rounded-lg p-3 border border-gray-100 dark:border-gray-800 shadow-sm flex flex-col min-h-[140px]'>
                                  <div className='flex items-baseline gap-1.5 mb-1 min-w-0'>
                                    <span className='text-xs font-semibold text-blue-600 dark:text-blue-400 shrink-0'>
                                      Day {idx + 1}
                                      {dayWorkouts.length > 1
                                        ? ` · ${wIdx + 1}/${dayWorkouts.length}`
                                        : ''}
                                    </span>
                                    <span
                                      className='text-sm font-medium text-gray-900 dark:text-gray-100 truncate'
                                      title={workoutData.name || ''}
                                    >
                                      {workoutData.name || ''}
                                    </span>
                                  </div>
                                  <div className='h-12 w-full'>
                                    <WorkoutChart
                                      workout={workoutData}
                                      createKey={`preview-${week.week}-${idx}-${wIdx}`}
                                    />
                                  </div>
                                  <div className='mt-auto pt-2 flex justify-between text-[10px] text-gray-500'>
                                    <span>{workoutData.duration ?? 0}m</span>
                                    <span>
                                      {formatDistance(
                                        (workoutData.distance ?? 0) * 1000,
                                        unitSystem
                                      )}
                                    </span>
                                  </div>
                                </div>
                              </WorkoutTooltip>
                            );
                          })}
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        </Tab.Panel>
        <Tab.Panel className='focus:outline-none'>
          <p className='text-gray-700 dark:text-gray-300 whitespace-pre-wrap leading-relaxed'>
            {plan.description || 'No description available.'}
          </p>
          {plan.coachName && (
            <div className='mt-6 pt-4 border-t border-gray-100 dark:border-gray-800'>
              <p className='text-xs text-gray-500'>Coach: {plan.coachName}</p>
            </div>
          )}
        </Tab.Panel>
      </Tab.Panels>
    </Tab.Group>
  );
}
